import { Button, ButtonGroup, Card, Dialog, Typography } from '@mui/material';
import { Box, Container, Stack } from '@mui/system';
import Grid from '@mui/system/Unstable_Grid/Grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MultiSelect } from './multivalue';
import { Seo } from 'src/components/seo';
import { useSettings } from 'src/hooks/use-settings';
import MonthEndAlertTable from './month-end-alert-table';
import { useDispatch, useSelector } from 'react-redux';
import {
  getMonthlyCloseOutAlert,
  resetUploadSignFileAction,
  reopenCloseoutAction,
  resetReopenCloseoutAction,
  createCustomCloseoutAction,
  resetCreateCustomCloseoutAction,
  updateCustomCloseoutAction,
  resetUpdateCustomCloseoutAction,
  cancelCustomCloseoutAction,
  resetCancelCustomCloseoutAction,
} from 'src/redux/actions/monthlyCloseOutAction';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router';
import SignatureAlertDocument from './SignatureAlertDocument';
import ReopenPeriodModal from './ReopenPeriodModal';
import BillingWarningModal from './BillingWarningModal';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { addYears, endOfMonth, format, isSameMonth, startOfMonth, subDays } from 'date-fns';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { customCloseoutTreatment } from 'src/config';
import { CUSTOM, EARLIEST_CLOSEOUT_YEAR, humanizeCloseoutError, toApiDate } from './customCloseout';
import CustomMonthPicker from './CustomMonthPicker';
import CustomPeriodBar from './CustomPeriodBar';
import EditCustomPeriodModal from './EditCustomPeriodModal';
import CancelCustomPeriodModal from './CancelCustomPeriodModal';
import { closeoutTestId, closeoutRowTestId } from './test-ids';

type Props = Record<string, never>;
const months = [
  { value: 'JANUARY', label: 'January' },
  { value: 'FEBRUARY', label: 'February' },
  { value: 'MARCH', label: 'March' },
  { value: 'APRIL', label: 'April' },
  { value: 'MAY', label: 'May' },
  { value: 'JUNE', label: 'June' },
  { value: 'JULY', label: 'July' },
  { value: 'AUGUST', label: 'August' },
  { value: 'SEPTEMBER', label: 'September' },
  { value: 'OCTOBER', label: 'October' },
  { value: 'NOVEMBER', label: 'November' },
  { value: 'DECEMBER', label: 'December' },
];

//  Derived from the current year so the list never goes stale, and floored at
//  EARLIEST_CLOSEOUT_YEAR, which the Custom range picker enforces too.
const years = Array.from(
  { length: new Date().getFullYear() - EARLIEST_CLOSEOUT_YEAR + 1 },
  (_, index) => {
    //  Newest first: the months a user filters by are almost always recent ones.
    const year = String(new Date().getFullYear() - index);
    return { value: year, label: year };
  }
);

//  How long a toast stays up. The cross-month create message asks the user to do something, so it
//  outlives the plain confirmations rather than disappearing mid-sentence.
const TOAST_MS = 2000;
const ACTIONABLE_TOAST_MS = 6000;

/**
 * BOPS-115 — CUSTOM answers for exactly one calendar month: the backend filters the periods it has
 * persisted by the month of `currentDate`. Handing it the Monthly tab's year-only date listed the
 * *next* January's periods under the selected year, so the month picker's own month is sent.
 *
 * End of month, not clamped to today: only the month is read off this date, and clamping the
 * current month to today would make the two branches differ for no gain. Module level so the
 * fetching effect and the refetch helper can't drift apart on how the date is derived.
 */
const customListDate = (month: Date) => toApiDate(endOfMonth(month));

/**
 * The month a `YYYY-MM-DD` range start falls in, read as a plain calendar date.
 *
 * `new Date('2026-07-01')` is parsed as UTC midnight, which is still June 30 anywhere west of
 * Greenwich — so "Manage on Custom range" on a July row landed the picker on June for every
 * US facility. The parts are read off the string instead of trusting the Date constructor.
 */
const monthOfRangeStart = (startDate?: string | Date) => {
  if (!startDate) return null;
  if (typeof startDate === 'string') {
    const [year, month, day] = startDate.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    return startOfMonth(new Date(year, month - 1, day));
  }
  return Number.isNaN(startDate.valueOf()) ? null : startOfMonth(startDate);
};

function getMonthIndex(monthName: string) {
  if (!monthName) return;
  const months: any = {
    JANUARY: 0,
    FEBRUARY: 1,
    MARCH: 2,
    APRIL: 3,
    MAY: 4,
    JUNE: 5,
    JULY: 6,
    AUGUST: 7,
    SEPTEMBER: 8,
    OCTOBER: 9,
    NOVEMBER: 10,
    DECEMBER: 11,
  };

  const monthIndex = months[monthName?.toUpperCase()];
  if (monthIndex === undefined) {
    throw new Error('Invalid month name');
  }
  return monthIndex;
}

function getFirstDateOfMonth(monthName: any, year: any) {
  return new Date(year, getMonthIndex(monthName), 1);
}

function getLastDateOfMonth(monthName: any, year: any) {
  const nextMonthIndex = getMonthIndex(monthName) + 1; // no need for module date math, its taken care of by new Date
  const d = new Date(year, nextMonthIndex, 1);
  return subDays(d, 1);
}

const WEEKLY = 'WEEKLY';
const MONTHLY = 'MONTHLY';
const MonthlyCloseoutAlert = (props: Props) => {
  const monthCloseOutResponse = useSelector((state: any) => state.monthlyCloseOutAlertReducer);
  const settings = useSettings();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  //  The tab / filters the alert view was opened from, handed back by its "Back to Closeout
  //  Alerts" link. Without it every return landed on Weekly, because the tab lives in local
  //  state that a remount resets — see the seeds below and `handleNavigateToMonthEndAlert`.
  const returnTo = (location?.state as any)?.returnTo;

  //  BOPS-115 — the Custom range tab ships behind its own toggle, off by default. `null` means the
  //  Split client hasn't settled; treat that as off so the tab never flashes in.
  const [customCloseoutToggle] = useFeatureToggleWhenReady(customCloseoutTreatment, 'ENTERPRISE');
  const isCustomCloseoutEnabled = customCloseoutToggle === true;

  interface ViewOption {
    label: string;
    value: string;
  }
  const viewOptions: ViewOption[] = [
    {
      label: 'Weekly',
      value: 'WEEKLY',
    },
    {
      label: 'Monthly',
      value: 'MONTHLY',
    },
    ...(isCustomCloseoutEnabled ? [{ label: 'Custom range', value: CUSTOM }] : []),
  ];

  const [activeButton, setActiveButton] = useState(returnTo?.division ?? WEEKLY);

  //  BOPS-118 — the Sign Certification redesign, gated for the frontend on the BOPS-115 treatment.
  //
  //  It can't be gated on the row the way the list's "Closed & signed by" line is: this modal opens
  //  on a period that has NOT been closed, so `signedByName` is necessarily absent there, and the
  //  redesign never appeared however the toggle was set. The backend's own `closeout-signed_by`
  //  isn't resolvable from the browser, so the two features that ship together for this pilot share
  //  one client-side switch — this treatment and nothing else, so an enterprise without the Custom
  //  range tab keeps the release-branch modal even when the backend is naming signers in the list.
  const isSignedByEnabled = isCustomCloseoutEnabled;

  //  BOPS-115 (8/8 mocks) — the Custom range tab filters by a single month rather than the Weekly
  //  Months + Years pair, so it keeps its own state. It defaults to the current month, and returns
  //  to it whenever the tab is opened.
  const [customMonth, setCustomMonth] = useState<Date>(() =>
    startOfMonth(returnTo?.customMonth ? new Date(returnTo.customMonth) : new Date())
  );

  /**
   * @param seedMonth the month the Custom range picker should open on. Only "Manage on Custom
   *   range" passes one: it comes off a specific Monthly row, so landing the picker on today
   *   would show a different month's periods than the row the user just clicked. A plain tab
   *   click passes nothing and keeps the current-month default.
   */
  const handleViewChange = (activeButtonName: string, seedMonth?: string | Date) => {
    setSelectMonth([]);
    setSelectYear([]);
    setActiveButton(activeButtonName);
    //  Custom's list is fetched by the effect below, off `customMonth` — dispatching here as well
    //  would ask for today's date first and then immediately for the month, fetching twice.
    if (activeButtonName === CUSTOM) {
      setCustomMonth(monthOfRangeStart(seedMonth) ?? startOfMonth(new Date()));
      return;
    }
    dispatch(
      getMonthlyCloseOutAlert({
        param: {
          currentDate: moment(new Date()).format('YYYY-MM-DD'),
          division: activeButtonName,
        },
      })
    );
  };

  const [selectMonth, setSelectMonth] = useState<any>(returnTo?.selectMonth ?? []);
  const [selectYear, setSelectYear] = useState<any>(returnTo?.selectYear ?? []);

  const getSelectedCurrentDate = () => {
    const now = new Date();
    if (selectMonth[0] && selectYear[0]) {
      const selectedNewDate = getLastDateOfMonth(selectMonth[0], selectYear[0]);
      return moment(selectedNewDate).format('YYYY-MM-DD');
    }
    if (selectMonth[0] && !selectYear[0]) {
      const selectedNewDate = getLastDateOfMonth(selectMonth[0], now.getFullYear());
      return selectedNewDate?.getMonth() === now.getMonth()
        ? moment(now).format('YYYY-MM-DD')
        : moment(selectedNewDate).format('YYYY-MM-DD');
    }
    if (!selectMonth[0] && selectYear[0]) {
      const fullDateByYear = addYears(new Date(selectYear[0], 0, 1), 1);
      return moment(fullDateByYear).format('YYYY-MM-DD');
    }
    return moment(now).format('YYYY-MM-DD');
  };

  const getCurrentDateForActiveTab = () =>
    activeButton === CUSTOM ? customListDate(customMonth) : getSelectedCurrentDate();

  const handleSelectMonth = useCallback((values: any): void => {
    const lastElement = values?.pop();
    const foundObject: any = months?.find((obj: any) => obj?.value === lastElement);

    if (foundObject) {
      setSelectMonth([foundObject?.value]);
    } else {
      setSelectMonth([]);
    }
  }, []);
  const handleSelectYear = useCallback((values: any): void => {
    const lastElement = values?.pop();
    const foundObject: any = years?.find((obj: any) => obj?.value === lastElement);

    if (foundObject) {
      setSelectYear([foundObject?.value]);
    } else {
      setSelectYear([]);
    }
  }, []);

  //  First load. `activeButton` is Weekly unless the alert view handed a tab back, in which case
  //  the restored tab owns the fetch — asking for Weekly here first would flash the wrong list.
  //  Custom and the Months / Years filters each have their own effect, so this stands down when
  //  either of those is what was restored, rather than fetching the same page twice.
  useEffect(() => {
    if (activeButton === CUSTOM) return;
    if (selectMonth[0] || selectYear[0]) return;
    dispatch(
      getMonthlyCloseOutAlert({
        param: {
          currentDate: moment(new Date()).format('YYYY-MM-DD'),
          division: activeButton,
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * BOPS-115 — Custom range fetches off its own month picker, which never leaves the tab. The
   * Weekly / Monthly effect below force-switches the active tab from its branches, so sharing it
   * would throw the user off Custom the moment they changed month.
   */
  useEffect(() => {
    if (activeButton !== CUSTOM) return;
    dispatch(
      getMonthlyCloseOutAlert({
        param: { currentDate: customListDate(customMonth), division: CUSTOM },
      })
    );
  }, [activeButton, customMonth, dispatch]);

  /**
   * BOPS-115 — a Split treatment can change mid-session, and this one gates a whole tab. Turning it
   * off only removes Custom range from `viewOptions`; a user already standing on that tab would
   * keep its create bar and month picker on screen with no tab highlighted, still polling a
   * division the facility is no longer entitled to. Fall back to Weekly instead.
   */
  useEffect(() => {
    //  `null` is the Split client still settling, not an answer. Treating it as off used to be
    //  harmless because nothing could open this screen on Custom — now the alert view can hand
    //  that tab back, and falling back here on mount would drop the user on Weekly before the
    //  toggle had even resolved. Only a resolved `false` withdraws the tab.
    if (customCloseoutToggle !== false || activeButton !== CUSTOM) return;
    handleViewChange(WEEKLY);
  }, [customCloseoutToggle]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeButton === CUSTOM) return;
    if (!!selectMonth[0] && !selectYear[0]) {
      const currentDate = new Date();
      const selectedNewDate = getLastDateOfMonth(selectMonth[0], currentDate.getFullYear());
      dispatch(
        getMonthlyCloseOutAlert({
          param: {
            currentDate:
              selectedNewDate?.getMonth() == currentDate?.getMonth()
                ? moment(currentDate).format('YYYY-MM-DD')
                : moment(selectedNewDate).format('YYYY-MM-DD'),
            division: 'WEEKLY',
          },
        })
      );
      setActiveButton(WEEKLY);
      return;
    }
    if (!!selectMonth[0] && !!selectYear[0]) {
      const selectedNewDate = getLastDateOfMonth(selectMonth[0], selectYear[0]);
      dispatch(
        getMonthlyCloseOutAlert({
          param: {
            currentDate: moment(selectedNewDate).format('YYYY-MM-DD'),
            division: 'WEEKLY',
          },
        })
      );
      setActiveButton(WEEKLY);
      return;
    }
    if (!selectMonth[0] && !!selectYear[0]) {
      const fullDateByYear = addYears(new Date(selectYear[0], 0, 1), 1);

      dispatch(
        getMonthlyCloseOutAlert({
          param: {
            currentDate: moment(fullDateByYear).format('YYYY-MM-DD'),
            division: 'MONTHLY',
          },
        })
      );
      setActiveButton(MONTHLY);
      return;
    }
  }, [selectMonth, selectYear]);

  //  `closeoutStatus` and `division` ride along so the alert view can tell both that the period is
  //  closed & signed and which kind of closeout it is — the range alone carries neither, and a
  //  weekly period can be closed & signed just like a hard close.
  const handleNavigateToMonthEndAlert = (dates: any, closeoutStatus?: string) => {
    navigate('/monthly-closeout-alerts/month-day-alert', {
      //  `returnTo` is opaque to the alert view — it echoes it back on Back so this list can
      //  reopen on the tab and filters the user left, instead of resetting to Weekly.
      state: {
        ...dates,
        closeoutStatus,
        division: activeButton,
        returnTo: {
          division: activeButton,
          selectMonth,
          selectYear,
          customMonth: customMonth.toISOString(),
        },
      },
    });
  };

  const [open, setOpen] = useState(false);
  const [signData, setSignData] = useState('');

  const [openBillingWarning, setOpenBillingWarning] = useState(false);
  const [billingWarningData, setBillingWarningData] = useState<any>(null);

  const handleClickOpen = (data: any) => {
    // For REOPEN status rows with billing changes, show warning first
    if (data?.status === 'REOPEN' && data?.hasBillingChangesWhileReopened) {
      setBillingWarningData(data);
      setOpenBillingWarning(true);
      return;
    }
    // Otherwise go directly to sign certification dialog
    setSignData(data);
    setOpen(true);
  };

  const handleBillingWarningClose = () => {
    setOpenBillingWarning(false);
    setBillingWarningData(null);
  };

  const handleBillingWarningConfirm = (alertData: any) => {
    setOpenBillingWarning(false);
    setBillingWarningData(null);
    // Proceed to sign certification dialog
    setSignData(alertData);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [openReopenModal, setOpenReopenModal] = useState(false);
  const [reopenAlertData, setReopenAlertData] = useState<any>(null);
  const reopenCloseoutState = useSelector((state: any) => state.reopenCloseoutReducer);

  const handleReopenClick = (data: any) => {
    setReopenAlertData(data);
    dispatch(resetReopenCloseoutAction());
    setOpenReopenModal(true);
  };

  const handleReopenModalClose = () => {
    if (!reopenCloseoutState?.loading) {
      setOpenReopenModal(false);
      setReopenAlertData(null);
      dispatch(resetReopenCloseoutAction());
    }
  };

  const handleReopenConfirm = () => {
    dispatch(resetReopenCloseoutAction());
    dispatch(
      reopenCloseoutAction({
        param: {
          startDate: reopenAlertData?.range?.startDate,
          endDate: reopenAlertData?.range?.endDate,
        },
      })
    );
  };

  useEffect(() => {
    if (!reopenCloseoutState?.getStatus && reopenCloseoutState?.response === null) return;
    if (reopenCloseoutState?.getStatus) {
      setOpenReopenModal(false);
      setReopenAlertData(null);
      setMessage(reopenCloseoutState?.response?.message || 'Closeout reopened successfully.');
      setSnackbarIsError(false);
      dispatch(
        getMonthlyCloseOutAlert({
          param: {
            currentDate: getCurrentDateForActiveTab(),
            division: activeButton,
          },
        })
      );
      setTimeout(() => {
        dispatch(resetReopenCloseoutAction());
        setMessage('');
      }, TOAST_MS);
    }
  }, [reopenCloseoutState]);
  const [message, setMessage] = useState('');
  const [snackbarIsError, setSnackbarIsError] = useState(false);
  const uploadSignatureFile = useSelector((state: any) => state.uploadSignatureFileReducer);

  // ---------------------------------------------------------------------------------------------
  //  BOPS-115 — custom closeout periods
  // ---------------------------------------------------------------------------------------------
  const createCustomState = useSelector((state: any) => state.createCustomCloseoutReducer);
  const updateCustomState = useSelector((state: any) => state.updateCustomCloseoutReducer);
  const cancelCustomState = useSelector((state: any) => state.cancelCustomCloseoutReducer);

  //  Bumped after a successful create so the New closeout period bar remounts with empty pickers.
  const [customBarKey, setCustomBarKey] = useState(0);
  const [editPeriodData, setEditPeriodData] = useState<any>(null);
  const [cancelPeriodData, setCancelPeriodData] = useState<any>(null);
  //  The range last submitted for creation. The success response doesn't echo it back, but the
  //  toast has to name the month it landed in when that isn't the month on screen.
  const [pendingCreateFrom, setPendingCreateFrom] = useState<Date | null>(null);

  const refetchCloseouts = useCallback(() => {
    dispatch(
      getMonthlyCloseOutAlert({
        param: { currentDate: getCurrentDateForActiveTab(), division: activeButton },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeButton, selectMonth, selectYear, customMonth]);

  useEffect(() => {
    if (createCustomState?.getStatus || createCustomState?.response == null) return;
    dispatch(resetCreateCustomCloseoutAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeButton, customMonth]);

  //  Server rejections are developer-facing strings (quoted ISO dates, log-style wording), so they
  //  are rewritten into user copy at the one place they enter the UI.
  const failureMessage = (mutationState: any, context: 'create' | 'edit' = 'create') =>
    !mutationState?.getStatus && mutationState?.response !== null
      ? humanizeCloseoutError(mutationState?.response?.message, context)
      : null;

  const handleCreateCustomPeriod = (from: Date, to: Date) => {
    dispatch(resetCreateCustomCloseoutAction());
    setPendingCreateFrom(from);
    dispatch(
      createCustomCloseoutAction({
        param: { startDate: toApiDate(from), endDate: toApiDate(to) },
      })
    );
  };

  const handleEditPeriodClick = (data: any) => {
    dispatch(resetUpdateCustomCloseoutAction());
    setEditPeriodData(data);
  };

  const handleEditPeriodConfirm = (newFrom: Date, newTo: Date) => {
    dispatch(resetUpdateCustomCloseoutAction());
    dispatch(
      updateCustomCloseoutAction({
        param: {
          //  The period is identified by the range it currently holds — there is no uuid on the wire.
          startDate: editPeriodData?.range?.startDate,
          endDate: editPeriodData?.range?.endDate,
          newStartDate: toApiDate(newFrom),
          newEndDate: toApiDate(newTo),
        },
      })
    );
  };

  const handleCancelPeriodClick = (data: any) => {
    dispatch(resetCancelCustomCloseoutAction());
    setCancelPeriodData(data);
  };

  const handleCancelPeriodConfirm = () => {
    dispatch(resetCancelCustomCloseoutAction());
    dispatch(
      cancelCustomCloseoutAction({
        param: {
          startDate: cancelPeriodData?.range?.startDate,
          endDate: cancelPeriodData?.range?.endDate,
        },
      })
    );
  };

  //  Each mutation settles the same way: on success announce it, refresh the list and close the
  //  surface that triggered it. Failures stay put so the message renders next to the inputs.
  /**
   * BOPS-115 (8/8 mocks) — a period can be created for a month other than the one the list is
   * showing, and the view deliberately does not jump to it: moving the list out from under the user
   * loses the month they were working. The toast names where it went and how to get there instead.
   */
  useEffect(() => {
    if (!createCustomState?.getStatus) return;
    const createdElsewhere = pendingCreateFrom && !isSameMonth(pendingCreateFrom, customMonth);
    const createdMonth = pendingCreateFrom ? format(pendingCreateFrom, 'MMMM yyyy') : '';
    setMessage(
      createdElsewhere
        ? `Closeout period created for ${createdMonth}. Switch the month picker to ${createdMonth} to see it.`
        : createCustomState?.response?.message || 'Closeout period created.'
    );
    setSnackbarIsError(false);
    setPendingCreateFrom(null);
    setCustomBarKey((key) => key + 1);
    refetchCloseouts();
    setTimeout(
      () => {
        dispatch(resetCreateCustomCloseoutAction());
        setMessage('');
      },
      createdElsewhere ? ACTIONABLE_TOAST_MS : TOAST_MS
    );
  }, [createCustomState?.getStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!updateCustomState?.getStatus) return;
    setMessage(updateCustomState?.response?.message || 'Closeout period updated.');
    setSnackbarIsError(false);
    setEditPeriodData(null);
    refetchCloseouts();
    setTimeout(() => {
      dispatch(resetUpdateCustomCloseoutAction());
      setMessage('');
    }, TOAST_MS);
  }, [updateCustomState?.getStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cancelCustomState?.getStatus) return;
    setMessage(cancelCustomState?.response?.message || 'Closeout period cancelled.');
    setSnackbarIsError(false);
    setCancelPeriodData(null);
    refetchCloseouts();
    setTimeout(() => {
      dispatch(resetCancelCustomCloseoutAction());
      setMessage('');
    }, TOAST_MS);
  }, [cancelCustomState?.getStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!uploadSignatureFile?.getStatus && uploadSignatureFile?.response === null) return;
    if (!uploadSignatureFile?.getStatus && uploadSignatureFile?.response !== null) {
      setOpen(false);
      setMessage(uploadSignatureFile?.response?.message);
      setSnackbarIsError(true);
      setTimeout(() => {
        dispatch(resetUploadSignFileAction());
        setMessage('');
      }, TOAST_MS);
      return;
    }
    setOpen(false);
    setMessage(uploadSignatureFile?.response?.message);
    setSnackbarIsError(false);
    dispatch(
      getMonthlyCloseOutAlert({
        param: {
          currentDate: getCurrentDateForActiveTab(),
          division: activeButton,
        },
      })
    );
    setTimeout(() => {
      dispatch(resetUploadSignFileAction());
      setMessage('');
    }, TOAST_MS);
  }, [uploadSignatureFile]);

  return (
    <>
      <Seo title="Restore-Insights: Monthly closeout alerts" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // py: 8,
          py: 3,
        }}
      >
        <Container maxWidth={settings.stretch ? false : 'xl'}>
          <Grid
            container
            disableEqualOverflow
            spacing={{
              xs: 3,
              lg: 5,
            }}
          >
            <Grid xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={4}
              >
                <Typography
                  variant="h4"
                  data-testid={closeoutTestId('heading')}
                >
                  Closeout Alerts
                </Typography>
              </Stack>
            </Grid>

            <Grid
              xs={12}
              md={12}
            >
              <ButtonGroup
                variant="contained"
                sx={{
                  border: '1px solid rgba(99, 102, 241, 0.50)',
                  borderRadius: '12px',
                  height: '36px',
                }}
                disabled={monthCloseOutResponse?.loading}
              >
                {viewOptions.map((option) => (
                  <Button
                    key={option.value}
                    onClick={() => handleViewChange(option.value)}
                    data-testid={closeoutRowTestId('view', option.value)}
                    sx={{
                      color: '#6366F1',
                      background: activeButton === option.value ? '#EBEEFE' : '#FFFFFF',
                      '&:hover': {
                        background: 'transparent',
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Grid>
            <Grid
              xs={12}
              md={12}
            >
              <Card sx={{ padding: '2rem' }}>
                {/*  BOPS-115 — on Custom range the create bar leads and the filters belong to the
                    list below it, so they sit under a "Custom closeouts" heading (per the mock).
                    Weekly and Monthly keep the filters at the top of the card as before. */}
                {activeButton === CUSTOM && (
                  <Box sx={{ px: '1rem' }}>
                    <CustomPeriodBar
                      key={customBarKey}
                      onCreate={handleCreateCustomPeriod}
                      creating={createCustomState?.loading}
                      serverError={failureMessage(createCustomState)}
                      onDismissServerError={() => dispatch(resetCreateCustomCloseoutAction())}
                    />
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5 }}
                    >
                      Custom closeouts
                    </Typography>
                  </Box>
                )}
                <Stack
                  gap="1rem"
                  sx={{ padding: '0 1rem 1rem 1rem' }}
                  flexDirection="row"
                >
                  {/*  BOPS-115 — one combined picker on Custom range, the Weekly-style pair
                      everywhere else. The two are not interchangeable: only Custom has to stop the
                      user reaching a month that hasn't happened. */}
                  {activeButton === CUSTOM ? (
                    <CustomMonthPicker
                      value={customMonth}
                      onChange={setCustomMonth}
                      disabled={monthCloseOutResponse?.loading}
                    />
                  ) : (
                    <>
                      {activeButton !== MONTHLY && (
                        <MultiSelect
                          label="Months"
                          onChange={handleSelectMonth}
                          options={months}
                          value={selectMonth}
                          testId="filter-months"
                        />
                      )}

                      <MultiSelect
                        label="Years"
                        onChange={handleSelectYear}
                        options={years}
                        value={selectYear}
                        testId="filter-years"
                      />
                    </>
                  )}
                </Stack>
                <Stack gap="1rem">
                  <MonthEndAlertTable
                    item={monthCloseOutResponse?.response?.data}
                    handleNavigateToMonthEndAlert={handleNavigateToMonthEndAlert}
                    handleClickOpen={handleClickOpen}
                    handleReopenClick={handleReopenClick}
                    division={activeButton}
                    handleEditPeriod={handleEditPeriodClick}
                    handleCancelPeriod={handleCancelPeriodClick}
                    handleGoToCustomTab={(row: any) =>
                      handleViewChange(CUSTOM, row?.range?.startDate)
                    }
                    customCloseoutEnabled={isCustomCloseoutEnabled}
                    //  Names the month only when the picker has been moved off its default. An
                    //  empty current month means none have been created; an empty month the user
                    //  navigated to means none *here*, and the two need different copy.
                    customFilterLabel={
                      activeButton === CUSTOM && !isSameMonth(customMonth, new Date())
                        ? format(customMonth, 'MMMM yyyy')
                        : null
                    }
                  />
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Dialog
        data-testid={closeoutTestId('sign-dialog')}
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="sm"
        sx={{ p: 2 }}
      >
        <SignatureAlertDocument
          handleClose={handleClose}
          signData={signData}
          division={activeButton}
          signedByEnabled={isSignedByEnabled}
        />
      </Dialog>
      <ReopenPeriodModal
        open={openReopenModal}
        onClose={handleReopenModalClose}
        alertData={reopenAlertData}
        onConfirm={handleReopenConfirm}
        confirmLoading={reopenCloseoutState?.loading}
        confirmError={
          !reopenCloseoutState?.getStatus && reopenCloseoutState?.response !== null
            ? reopenCloseoutState?.response?.message || 'Failed to reopen closeout.'
            : null
        }
      />
      <BillingWarningModal
        open={openBillingWarning}
        onClose={handleBillingWarningClose}
        alertData={billingWarningData}
        onConfirmClose={handleBillingWarningConfirm}
      />
      <EditCustomPeriodModal
        open={!!editPeriodData}
        onClose={() => {
          if (updateCustomState?.loading) return;
          setEditPeriodData(null);
          dispatch(resetUpdateCustomCloseoutAction());
        }}
        alertData={editPeriodData}
        onConfirm={handleEditPeriodConfirm}
        confirmLoading={updateCustomState?.loading}
        confirmError={failureMessage(updateCustomState, 'edit')}
      />
      <CancelCustomPeriodModal
        open={!!cancelPeriodData}
        onClose={() => {
          if (cancelCustomState?.loading) return;
          setCancelPeriodData(null);
          dispatch(resetCancelCustomCloseoutAction());
        }}
        alertData={cancelPeriodData}
        onConfirm={handleCancelPeriodConfirm}
        confirmLoading={cancelCustomState?.loading}
        confirmError={failureMessage(cancelCustomState)}
      />
      {message !== '' && (
        <SimpleSnackbar
          message={message}
          isError={snackbarIsError}
        />
      )}
    </>
  );
};

export default MonthlyCloseoutAlert;
